import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import FileSaver from 'file-saver'
import log from 'loglevel'
import Papa from 'papaparse'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import {
  CreateVendorRequest,
  CreateVendorsRequest,
  DeleteVendorRequest,
  GetVendorRequest,
  ListVendorsRequest,
  ListVendorsResponse,
  UpdateVendorRequest,
  UpdateVendorsRequest,
  VendorRecord,
  ATTESTATION_STATUS,
} from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import {
  RISK_LINK_TYPE,
  RiskLink,
} from '@trustero/trustero-api-web/lib/risk/risk_pb'
import {
  AddAttestationRequest,
  AddAttestationsRequest,
  AttestationRecord,
  Attestations,
  DeleteAttestationRequest,
  ListAttestationsRequest,
  UpdateAttestationRequest,
} from '@trustero/trustero-api-web/lib/vendormanagement/attestation_pb'
import { VendorPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_grpc_web_pb'
import { AttestationPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/attestation_grpc_web_pb'
import { RequestPromiseClient } from '@trustero/trustero-api-web/lib/request/request_grpc_web_pb'
import { QuestionBankServicePromiseClient } from '@trustero/trustero-api-web/lib/questionbank/questionbank_grpc_web_pb'
import {
  GetQuestionBankQuestionRequest,
  ListQuestionBankModulesRequest,
  ListQuestionBankModulesResponse,
  ListQuestionBankQuestionsByModuleRequest,
  ListQuestionnaireTemplateQuestionsRequest,
  ListQuestionnaireTemplateSectionsRequest,
  ListQuestionnaireTemplatesRequest,
  ListQuestionnaireTemplatesResponse,
  QuestionBankModule,
  QuestionBankQuestion,
  QuestionnaireTemplate,
  QuestionnaireTemplateQuestion,
  QuestionnaireTemplateSection,
} from '@trustero/trustero-api-web/lib/questionbank/questionbank_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useGrpcRevalidateByMethod } from 'src/components'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { useConfirmationContext } from 'src/Confirmation'
import { useFetchAttestationBody } from 'src/components/async/document/useDocument'
import { useThrobberContext } from 'src/Throbber/ThrobberContext'
import { capitalizeFirstLetter } from 'src/Utils/globalHelpers'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useConfirmationModal } from 'src/components/ModalForms'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { useToggleLinks } from '../Risks/risks.hooks'
import {
  RISK_LEVEL_TO_LABEL,
  AttestationStatusLabels,
} from './vendors.constants'
import { applyVendorsFilters, applyVendorsSort } from './vendors.helpers'

const ATTESTATION_STATUS_TO_LABEL: Record<
  ATTESTATION_STATUS,
  AttestationStatusLabels
> = {
  [ATTESTATION_STATUS.NONE]: AttestationStatusLabels.NONE,
  [ATTESTATION_STATUS.STALE]: AttestationStatusLabels.STALE,
  [ATTESTATION_STATUS.FRESH]: AttestationStatusLabels.FRESH,
}

export const useDownloadVendorCSV = (): ((vendors: VendorRecord[]) => void) => {
  return useCallback((vendors: VendorRecord[]) => {
    const data = vendors.map((vendor) => ({
      Name: vendor.getName(),
      'Risk Tier': RISK_LEVEL_TO_LABEL[vendor.getRiskLevel()],
      Attestation: ATTESTATION_STATUS_TO_LABEL[vendor.getAttestationStatus()],
      'Related Risks': vendor.getRisksCount(),
      Owner: vendor.getOwnerEmail(),
      Assignees: vendor.getAssigneeEmailsList().join(','),
    }))

    const csv = Papa.unparse(data)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(blob, 'vendor-management.csv')
  }, [])
}

export const useInvalidateVendorsCache = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    try {
      await Promise.all([
        mutateFunc(VendorPromiseClient.prototype.listVendors),
        mutateFunc(VendorPromiseClient.prototype.getVendor),
        mutateFunc(AttestationPromiseClient.prototype.listAttestations),
        mutateFunc(RequestPromiseClient.prototype.listRequestAttestations),
      ])
    } catch (error) {
      log.error('Error when invalidating vendors cache', error)
    }
  }, [mutateFunc])
}

export const useVendorsRequest = (): ListVendorsRequest => {
  const location = useLocation()

  return useMemo(() => {
    const req = new ListVendorsRequest()
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      {
        arrayFormat: 'bracket',
      },
    )
    // Apply all filters for each parameter in URL
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyVendorsFilters(req, queryParams, filterType),
    )
    applyVendorsSort(req, queryParams)
    return req
  }, [location.search])
}

export const useVendor = (
  vendorId: string,
  shouldFetch = true,
): GrpcResponse<VendorRecord> => {
  const request = new GetVendorRequest().setId(vendorId)
  const { response } = useSwrImmutableGrpc(
    VendorPromiseClient.prototype.getVendor,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useVendors = (
  req: ListVendorsRequest = new ListVendorsRequest(),
  shouldFetch = true,
): GrpcResponse<ListVendorsResponse> => {
  const { response } = useSwrImmutableGrpc(
    VendorPromiseClient.prototype.listVendors,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// Read-only list of the fixed Question Bank / SIG Lite / CAIQ questionnaire
// templates shown in the Vendors index "Questionnaire Templates" tab.
export const useQuestionnaireTemplates = (
  shouldFetch = true,
): GrpcResponse<ListQuestionnaireTemplatesResponse> => {
  const { response } = useSwrImmutableGrpc(
    QuestionBankServicePromiseClient.prototype.listQuestionnaireTemplates,
    new ListQuestionnaireTemplatesRequest(),
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// Read-only fetch of the bank's modules plus its overall last-modified
// timestamp, for the "Question Bank" row's Date Modified column.
export const useQuestionBankModules = (
  shouldFetch = true,
): GrpcResponse<ListQuestionBankModulesResponse> => {
  const { response } = useSwrImmutableGrpc(
    QuestionBankServicePromiseClient.prototype.listQuestionBankModules,
    new ListQuestionBankModulesRequest(),
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export type QuestionBankModuleContents = {
  module: QuestionBankModule
  questions: QuestionBankQuestion[]
}

/**
 * Fetches every Question Bank module along with its questions, for the
 * read-only Question Bank view page. No SWR here since this is a two-level
 * fan-out (list modules, then list questions per module) rather than a
 * single grpc call.
 */
export const useQuestionBankContents = (
  shouldFetch = true,
): {
  data: QuestionBankModuleContents[]
  isLoading: boolean
  error: unknown
} => {
  const client = useAuthorizedGrpcClient(QuestionBankServicePromiseClient)
  const [data, setData] = useState<QuestionBankModuleContents[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<unknown>(undefined)

  useEffect(() => {
    if (!shouldFetch) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    ;(async () => {
      try {
        const modulesRes = await client.listQuestionBankModules(
          new ListQuestionBankModulesRequest(),
        )
        const contents = await Promise.all(
          modulesRes.getModulesList().map(async (mod) => {
            const questionsRes = await client.listQuestionBankQuestionsByModule(
              new ListQuestionBankQuestionsByModuleRequest().setModuleId(
                mod.getId(),
              ),
            )
            return { module: mod, questions: questionsRes.getQuestionsList() }
          }),
        )
        if (!cancelled) setData(contents)
      } catch (err) {
        if (!cancelled) setError(err)
        log.error('Error loading question bank contents', err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [client, shouldFetch])

  return { data, isLoading, error }
}

export type QuestionnaireTemplateQuestionContents = {
  question: QuestionnaireTemplateQuestion
  // The response type/options come from the mapped Question Bank question --
  // how the question is meant to be answered is real content worth showing,
  // even though the mapping/synthesis-rule that derives it is not.
  bankQuestion: QuestionBankQuestion | undefined
}

export type QuestionnaireTemplateSectionContents = {
  section: QuestionnaireTemplateSection
  questions: QuestionnaireTemplateQuestionContents[]
}

/**
 * Resolves a template by its real name ('SIG Lite' | 'CAIQ'), then fetches its
 * sections/questions, plus each question's response type/options via its
 * mapped Question Bank question. The synthesis rule/mapping itself is
 * internal semantics and isn't exposed here.
 */
export const useQuestionnaireTemplateContents = (
  templateName: string | undefined,
): {
  template: QuestionnaireTemplate | undefined
  sections: QuestionnaireTemplateSectionContents[]
  isLoading: boolean
  error: unknown
} => {
  const client = useAuthorizedGrpcClient(QuestionBankServicePromiseClient)
  const { data: templatesData, loading: templatesLoading } =
    useQuestionnaireTemplates(!!templateName)
  const [sections, setSections] = useState<
    QuestionnaireTemplateSectionContents[]
  >([])
  const [contentsLoading, setContentsLoading] = useState(true)
  const [error, setError] = useState<unknown>(undefined)

  const template = useMemo(() => {
    if (!templateName || !templatesData) return undefined
    return templatesData
      .getTemplatesList()
      .find((t) => t.getName() === templateName)
  }, [templateName, templatesData])

  useEffect(() => {
    if (!template) {
      setContentsLoading(false)
      return
    }
    let cancelled = false
    setContentsLoading(true)
    ;(async () => {
      try {
        const sectionsRes = await client.listQuestionnaireTemplateSections(
          new ListQuestionnaireTemplateSectionsRequest().setTemplateId(
            template.getId(),
          ),
        )
        const bankQuestionCache = new Map<string, QuestionBankQuestion>()
        const getBankQuestion = async (
          bankQuestionId: string,
        ): Promise<QuestionBankQuestion> => {
          const cached = bankQuestionCache.get(bankQuestionId)
          if (cached) return cached
          const bankQuestion = await client.getQuestionBankQuestion(
            new GetQuestionBankQuestionRequest().setId(bankQuestionId),
          )
          bankQuestionCache.set(bankQuestionId, bankQuestion)
          return bankQuestion
        }

        const contents = await Promise.all(
          sectionsRes.getSectionsList().map(async (section) => {
            const questionsRes =
              await client.listQuestionnaireTemplateQuestions(
                new ListQuestionnaireTemplateQuestionsRequest().setSectionId(
                  section.getId(),
                ),
              )
            const questions = await Promise.all(
              questionsRes.getQuestionsList().map(async (question) => {
                const bankQuestionId = question
                  .getMappingsList()[0]
                  ?.getQuestionBankQuestionId()
                const bankQuestion = bankQuestionId
                  ? await getBankQuestion(bankQuestionId)
                  : undefined
                return { question, bankQuestion }
              }),
            )
            return { section, questions }
          }),
        )

        if (!cancelled) {
          setSections(contents)
        }
      } catch (err) {
        if (!cancelled) setError(err)
        log.error('Error loading questionnaire template contents', err)
      } finally {
        if (!cancelled) setContentsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [client, template])

  return {
    template,
    sections,
    isLoading: templatesLoading || contentsLoading,
    error,
  }
}

export type QuestionnaireTemplateCounts = {
  moduleCount: number
  questionCount: number
  isLoading: boolean
}

/**
 * Counts of a template's sections ("modules") and total questions, for the
 * Questionnaire Templates tab's Modules/Questions columns. Only fetches
 * sections/questions, not the mapped bank questions -- these are just counts.
 */
export const useQuestionnaireTemplateCounts = (
  templateId: string | undefined,
): QuestionnaireTemplateCounts => {
  const client = useAuthorizedGrpcClient(QuestionBankServicePromiseClient)
  const [moduleCount, setModuleCount] = useState(0)
  const [questionCount, setQuestionCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!templateId) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    ;(async () => {
      try {
        const sectionsRes = await client.listQuestionnaireTemplateSections(
          new ListQuestionnaireTemplateSectionsRequest().setTemplateId(
            templateId,
          ),
        )
        const sections = sectionsRes.getSectionsList()
        const questionCounts = await Promise.all(
          sections.map(async (section) => {
            const questionsRes =
              await client.listQuestionnaireTemplateQuestions(
                new ListQuestionnaireTemplateQuestionsRequest().setSectionId(
                  section.getId(),
                ),
              )
            return questionsRes.getQuestionsList().length
          }),
        )
        if (!cancelled) {
          setModuleCount(sections.length)
          setQuestionCount(questionCounts.reduce((a, b) => a + b, 0))
        }
      } catch (err) {
        log.error('Error loading questionnaire template counts', err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [client, templateId])

  return { moduleCount, questionCount, isLoading }
}

export const useBulkCreateVendors = (): ((
  requests: CreateVendorRequest[],
) => Promise<void>) => {
  const mutate = useInvalidateVendorsCache()
  const vendorClient = useAuthorizedGrpcClient(VendorPromiseClient)

  return async (requests: CreateVendorRequest[]) => {
    const request = new CreateVendorsRequest().setRequestsList(requests)
    await vendorClient.createVendors(request)
    await mutate()
  }
}

export const useUpdateVendors = (): ((
  requests: UpdateVendorRequest[],
) => Promise<void>) => {
  const mutate = useInvalidateVendorsCache()
  const vendorClient = useAuthorizedGrpcClient(VendorPromiseClient)

  return async (requests: UpdateVendorRequest[]) => {
    const updates = new UpdateVendorsRequest().setRequestsList(requests)
    await vendorClient.updateVendors(updates)
    await mutate()
  }
}

export const useToggleVendorReviewed = (
  vendor: VendorRecord,
): {
  isReviewed: boolean
  toggleReviewed: (e?: React.MouseEvent) => Promise<void>
} => {
  const updateVendors = useUpdateVendors()
  const lastReviewedAt = vendor.getLastReviewedAt()
  const isReviewed = lastReviewedAt
    ? lastReviewedAt.getSeconds() > 0 || lastReviewedAt.getNanos() > 0
    : false

  const toggleReviewed = useCallback(
    async (e?: React.MouseEvent) => {
      try {
        e?.preventDefault()
        const update = new UpdateVendorRequest()
          .setId(vendor.getId())
          .setLastReviewedAt(
            isReviewed
              ? new Timestamp().setSeconds(0)
              : Timestamp.fromDate(new Date()),
          )

        await updateVendors([update])
      } catch (err) {
        log.error('Failed to toggle vendor reviewed status', err)
        showInfoToast('Failed to update vendor review status')
      }
    },
    [vendor, updateVendors, isReviewed],
  )

  return { isReviewed, toggleReviewed }
}

/**
 * Custom hook to delete a vendor.
 *
 * @returns A function that deletes a vendor.
 */
export const useDeleteVendor = (): ((id: string) => Promise<void>) => {
  const { setConfirmationState } = useConfirmationContext()
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const mutate = useInvalidateVendorsCache()
  const { startThrobber, stopThrobber } = useThrobberContext()

  return async (id: string) => {
    setConfirmationState({
      isShown: true,
      size: 'lg',
      title: 'Are you sure you want to delete this vendor?',
      body: 'This will delete any attestations and links to risks.',
      confirmText: 'Delete Vendor',
      onConfirmCB: async () => {
        try {
          startThrobber()
          await client.deleteVendor(new DeleteVendorRequest().setId(id))
        } catch (err) {
          log.error('Error deleting vendor', err)
          setConfirmationState({
            isShown: false,
            title: '',
            body: '',
            confirmText: '',
            onConfirmCB: () => null,
          })
          showInfoToast(
            'There was an error deleting the vendor. Please try again.',
          )
        } finally {
          await mutate()
          stopThrobber()
        }
      },
    })
  }
}

export const useAttestations = (
  vendorId: string,
  shouldFetch = true,
): GrpcResponse<Attestations> => {
  const request = new ListAttestationsRequest().setVendorId(vendorId)
  const { response } = useSwrImmutableGrpc(
    AttestationPromiseClient.prototype.listAttestations,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useAddAttestation = (): (({
  description,
  reportType,
  expirationDate,
  vendorId,
  vendorName,
  file,
}: {
  description: string
  reportType: string
  expirationDate: Date
  vendorId: string
  vendorName: string
  file: File
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AttestationPromiseClient)
  const mutate = useInvalidateVendorsCache()

  return async ({
    description,
    reportType,
    expirationDate,
    vendorId,
    vendorName,
    file,
  }) => {
    const attestation = new AttestationRecord()
      .setDescription(description)
      .setReportType(reportType)
      .setExpirationDate(Timestamp.fromDate(expirationDate))
      .setVendorId(vendorId)
      .setMime(file.type)
    const attestationReq = new AddAttestationRequest()
      .setAttestation(attestation)
      .setBody(new Uint8Array(await new Response(file).arrayBuffer()))
      .setVendorName(vendorName)
    const req = new AddAttestationsRequest().setRequestsList([attestationReq])
    await client.addAttestations(req)
    await mutate()
  }
}

/**
 * Custom hook to delete an attestation.
 *
 * @returns A function that deletes an attestation.
 */
export const useDeleteAttestation = (): ((id: string) => Promise<void>) => {
  const { setConfirmationState } = useConfirmationContext()
  const client = useAuthorizedGrpcClient(AttestationPromiseClient)
  const mutate = useInvalidateVendorsCache()
  const { startThrobber, stopThrobber } = useThrobberContext()

  return async (id: string) => {
    setConfirmationState({
      isShown: true,
      size: 'lg',
      title: 'Delete Attestation?',
      body: 'This will remove the attestation from this vendor.',
      confirmText: 'Delete',
      onConfirmCB: async () => {
        try {
          startThrobber()
          await client.deleteAttestation(
            new DeleteAttestationRequest().setId(id),
          )
        } catch (err) {
          log.error('Error deleting attestation', err)
          setConfirmationState({
            isShown: false,
            title: '',
            body: '',
            confirmText: '',
            onConfirmCB: () => null,
          })
          showInfoToast(
            'There was an error deleting the attestation. Please try again.',
          )
        } finally {
          await mutate()
          stopThrobber()
        }
      },
    })
  }
}

export const useDownloadAttestation = (
  contentId: string,
  description: string,
): (() => Promise<void>) => {
  const fetchBody = useFetchAttestationBody(contentId, 'pdf')

  return async () => {
    try {
      const body = await fetchBody()
      if (!(body instanceof Uint8Array)) {
        showInfoToast(
          'There was a problem downloading the attestation. Please try again.',
        )
        return
      }
      const blob = new Blob([body], {
        type: 'application/pdf',
      })
      FileSaver.saveAs(blob, `${description}.pdf`)
    } catch (err) {
      log.error('Error downloading attestation', err)
      showInfoToast(
        'There was an error downloading the attestation. Please try again.',
      )
    }
  }
}

export const useUnlinkVendorRiskModal = (
  vendorId: string,
  vendorName: string,
  riskId: string,
  riskCustomId: string,
  riskName: string,
  subjectType: MODEL_TYPE = MODEL_TYPE.VENDOR,
): (() => void) => {
  const unlinkFunc = useToggleLinks()
  const mutate = useInvalidateVendorsCache()
  const isVendorSubject = subjectType === MODEL_TYPE.VENDOR
  const subject = isVendorSubject ? 'vendor' : 'risk'
  const attachment = isVendorSubject ? 'risk' : 'vendor'
  const subjectId = isVendorSubject ? vendorId : riskId
  const riskText = `${riskCustomId} ${riskName}`
  const title = isVendorSubject
    ? `Unlink Risk from ${vendorName}?`
    : `Unlink ${vendorName} from this Risk?`
  const body = isVendorSubject
    ? `This will remove ${riskText}`
    : `This will remove ${vendorName} from ${riskText}`

  const unlinkVendor = useCallback(async () => {
    try {
      await unlinkFunc({
        type: RISK_LINK_TYPE.UNLINK,
        links: [
          new RiskLink()
            .setId(vendorId)
            .setModelType(MODEL_TYPE.VENDOR)
            .setRiskId(riskId),
        ],
      })
      await mutate()
    } catch (err) {
      log.error(
        `Error unlinking ${attachment} from ${subject} id ${subjectId}`,
        err,
      )
      showInfoToast(
        `There was an error unlinking the ${attachment} from the ${subject}. Please try again.`,
      )
    }
  }, [unlinkFunc, attachment, subject, subjectId, riskId, vendorId, mutate])

  const confirmationModalProps = {
    title: title,
    body: body,
    confirmText: `Unlink ${capitalizeFirstLetter(attachment)}`,
    onConfirmCB: unlinkVendor,
  }

  return useConfirmationModal(confirmationModalProps)
}

export type UpdateAttestation = {
  id: string
  description: string
  reportType: string
  expirationDate: Date
}

export const useUpdateAttestation = (): ((
  attestation: UpdateAttestation,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AttestationPromiseClient)
  const mutate = useInvalidateVendorsCache()

  return async ({ id, description, reportType, expirationDate }) => {
    const attestation = new AttestationRecord()
      .setDescription(description)
      .setReportType(reportType)
      .setExpirationDate(Timestamp.fromDate(expirationDate))
      .setId(id)
    const req = new UpdateAttestationRequest().setAttestation(attestation)
    await client.updateAttestation(req)
    await mutate()
  }
}

export const useSearchableVendors = (
  vendors: VendorRecord[],
): SearchableData<VendorRecord>[] => {
  return useMemo(() => {
    return prepareSearchableData(
      vendors,
      (vendor: VendorRecord) => vendor.getId(),
      (vendor: VendorRecord) => {
        const name = vendor.getName()
        const riskTier = vendor.getRiskLevel()
        const riskTierText = RISK_LEVEL_TO_LABEL[riskTier]
        const relatedRisk = vendor.getRisksCount().toString()
        const owner = vendor.getOwnerEmail()

        return [name, riskTierText, relatedRisk, owner]
      },
    )
  }, [vendors])
}
